import { useMetaData } from './useMetaData.js'
import { useGoalsData } from './useGoalsData.js'
import { useActionItemOp } from '../mutations/useActionItemOp.js'
import { useUIStore } from '../store/uiStore.js'
import { goalKey, pillarIndexByName } from '../data/metaKeys.js'
import { getAuthoritative } from '../data/actionItemOverrides.js'

/**
 * useActionItems — read + mutate action items for a specific goal.
 *
 * Keyed by `p{pi}-g{gi}` in meta.actionItems. Each item has:
 *   { id, task, owner, dueDate, status, createdAt, createdBy,
 *     completedAt, lastUpdate, lastUpdateAt, lastUpdateBy }
 *
 * Returns { items, create, toggle, update, remove }.
 *
 * Durability note: this hook used to route mutations through useUpdateMeta,
 * which re-saves the FULL meta blob. That meant a concurrent unrelated
 * writer could resurrect deleted items — they'd POST meta with a
 * stale action-items list (KV eventual consistency). We now call the
 * dedicated /meta/actionItems endpoint that mutates the list server-side,
 * so deletes are permanent even when other writers are mid-save.
 */
export function useActionItems(pillarName, goalIndex) {
  const currentUser = useUIStore((s) => s.currentUser) || 'Unknown'
  const { data: goalsData } = useGoalsData()
  const { data: meta } = useMetaData()
  const opAction = useActionItemOp()

  const pi = pillarIndexByName(goalsData, pillarName)
  const key = pi >= 0 ? goalKey(pi, goalIndex) : null
  // Prefer the short-lived authoritative override if we just wrote to
  // this goal's list — it shields us from a stale KV refetch briefly
  // resurrecting items we just removed.
  const override = key ? getAuthoritative(key) : null
  const items = override ?? ((key && meta.actionItems?.[key]) || [])

  // Small ID generator (base36 timestamp + random) — matches q2-goals.html
  const newId = () =>
    Date.now().toString(36) + Math.random().toString(36).slice(2, 5)

  const create = ({ task, owner, dueDate }) => {
    if (!key || !task?.trim()) return
    const item = {
      id: newId(),
      task: task.trim(),
      owner: owner || '—',
      dueDate: dueDate || '',
      status: 'open',
      createdAt: new Date().toISOString(),
      createdBy: currentUser,
    }
    opAction.mutate({
      op: 'create',
      goalKey: key,
      item,
      optimistic: (list) => [...list, item],
    })
  }

  const toggle = (itemId) => {
    if (!key) return
    opAction.mutate({
      op: 'toggle',
      goalKey: key,
      itemId,
      optimistic: (list) =>
        list.map((a) =>
          a.id === itemId
            ? {
                ...a,
                status: a.status === 'done' ? 'open' : 'done',
                completedAt:
                  a.status === 'done' ? null : new Date().toISOString(),
              }
            : a
        ),
    })
  }

  const remove = (itemId) => {
    if (!key) return
    opAction.mutate({
      op: 'remove',
      goalKey: key,
      itemId,
      optimistic: (list) => list.filter((a) => a.id !== itemId),
    })
  }

  const update = (itemId, updateText) => {
    if (!key) return
    const patch = {
      lastUpdate: updateText,
      lastUpdateAt: new Date().toISOString(),
      lastUpdateBy: currentUser,
    }
    opAction.mutate({
      op: 'update',
      goalKey: key,
      itemId,
      patch,
      optimistic: (list) =>
        list.map((a) => (a.id === itemId ? { ...a, ...patch } : a)),
    })
  }

  return { items, create, toggle, remove, update }
}
